document.documentElement.classList.add('js');

(() => {
  const body = document.body;
  const html = document.documentElement;
  const designMode = Boolean(window.Shopify?.designMode);
  const rtl = html.dir === 'rtl';
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new WeakMap();
  const motionControllers = new WeakMap();
  if (designMode) body.classList.add('is-design-mode');

  const motion = () => {
    const intensity = body.dataset.motionIntensity || 'balanced';
    return {
      enabled: body.dataset.motion === 'true' && intensity !== 'off' && !reducedMotionQuery.matches,
      intensity,
      reveal: body.dataset.revealMotion === 'true',
      imageReveal: body.dataset.imageRevealEnabled === 'true',
      cart: body.dataset.cartAnimation === 'true'
    };
  };

  const selectAll = (scope, selector) => {
    const matches = scope instanceof Element && scope.matches(selector) ? [scope] : [];
    return [...matches, ...scope.querySelectorAll(selector)];
  };

  const on = (target, event, callback, signal, options = {}) => {
    if (!target) return;
    target.addEventListener(event, callback, { ...options, signal });
  };

  const focusable = (container) => [...container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((element) => !element.hidden && element.offsetParent !== null);

  const trapFocus = (event, container) => {
    if (event.key !== 'Tab') return;
    const items = focusable(container);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(html.lang || 'he', { style: 'currency', currency }).format(cents / 100);
    } catch (_) {
      return `${(cents / 100).toFixed(2)} ${currency || ''}`.trim();
    }
  };

  const fetchCart = async () => {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  };

  const changeCartLine = async (line, quantity) => {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity) })
    });
    if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
    return response.json();
  };

  const buildCartLine = (item, index, header, currency) => {
    const article = document.createElement('article');
    article.dataset.cartLine = String(index + 1);
    const mediaLink = document.createElement('a');
    mediaLink.href = item.url;
    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.product_title;
      image.loading = 'lazy';
      mediaLink.append(image);
    }
    const info = document.createElement('div');
    const number = document.createElement('span');
    number.className = 'cart-drawer__index';
    number.textContent = String(index + 1).padStart(2, '0');
    const title = document.createElement('a');
    title.href = item.url;
    title.textContent = item.product_title;
    title.dir = 'auto';
    const variant = document.createElement('small');
    variant.textContent = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
    const quantity = document.createElement('div');
    quantity.className = 'cart-drawer__quantity';
    const decrease = document.createElement('button');
    decrease.type = 'button';
    decrease.dataset.cartQuantity = '';
    decrease.dataset.line = String(index + 1);
    decrease.dataset.quantity = String(Math.max(0, item.quantity - 1));
    decrease.setAttribute('aria-label', header.dataset.decreaseLabel || 'Decrease quantity');
    decrease.textContent = '−';
    const quantityText = document.createElement('span');
    quantityText.textContent = `×${item.quantity}`;
    const increase = document.createElement('button');
    increase.type = 'button';
    increase.dataset.cartQuantity = '';
    increase.dataset.line = String(index + 1);
    increase.dataset.quantity = String(item.quantity + 1);
    increase.setAttribute('aria-label', header.dataset.increaseLabel || 'Increase quantity');
    increase.textContent = '+';
    quantity.append(decrease, quantityText, increase);
    const price = document.createElement('span');
    price.textContent = formatMoney(item.final_line_price, currency);
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'cart-drawer__remove';
    remove.dataset.cartRemove = '';
    remove.dataset.line = String(index + 1);
    remove.textContent = header.dataset.removeLabel;
    info.append(number, title, variant, quantity, price, remove);
    article.append(mediaLink, info);
    return article;
  };

  const renderCart = (header, cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((counter) => {
      counter.textContent = `[${cart.item_count}]`;
      counter.classList.remove('is-updated');
      window.requestAnimationFrame(() => counter.classList.add('is-updated'));
    });
    header.querySelectorAll('[data-cart-drawer-count]').forEach((counter) => { counter.textContent = cart.item_count; });
    const content = header.querySelector('[data-cart-content]');
    if (!content) return;
    content.replaceChildren();
    if (!cart.items.length) {
      const empty = document.createElement('div');
      empty.className = 'cart-drawer__empty';
      const text = document.createElement('p');
      text.textContent = header.dataset.cartEmptyLabel;
      const link = document.createElement('a');
      link.className = 'text-link';
      link.href = header.dataset.shopUrl;
      link.textContent = header.dataset.continueLabel;
      empty.append(text, link);
      content.append(empty);
      return;
    }
    const lines = document.createElement('div');
    lines.className = 'cart-drawer__lines';
    cart.items.forEach((item, index) => lines.append(buildCartLine(item, index, header, cart.currency)));
    const footer = document.createElement('div');
    footer.className = 'cart-drawer__footer';
    const subtotal = document.createElement('div');
    const subtotalLabel = document.createElement('span');
    subtotalLabel.textContent = header.dataset.subtotalLabel;
    const subtotalValue = document.createElement('strong');
    subtotalValue.dataset.cartSubtotal = '';
    subtotalValue.textContent = formatMoney(cart.total_price, cart.currency);
    subtotal.append(subtotalLabel, subtotalValue);
    const viewCart = document.createElement('a');
    viewCart.className = 'button button--outline';
    viewCart.href = header.dataset.cartUrl;
    viewCart.textContent = header.dataset.viewCartLabel;
    const checkout = document.createElement('a');
    checkout.className = 'button';
    checkout.href = header.dataset.checkoutUrl;
    checkout.textContent = header.dataset.checkoutLabel;
    footer.append(subtotal, viewCart, checkout);
    content.append(lines, footer);
  };

  const initHeader = (header) => {
    if (controllers.has(header)) return controllers.get(header);
    const abort = new AbortController();
    const { signal } = abort;
    const toggle = header.querySelector('[data-nav-toggle]');
    const nav = header.querySelector('[data-nav]');
    const cartLink = header.querySelector('[data-cart-link]');
    const cartDrawer = header.querySelector('[data-cart-drawer]');
    const cartOverlay = header.querySelector('[data-cart-overlay]');
    const cartClose = header.querySelector('[data-cart-close]');
    let menuReturnFocus = null;
    let cartReturnFocus = null;

    const setMenuOpen = (open) => {
      if (!toggle || !nav) return;
      if (open) menuReturnFocus = document.activeElement;
      nav.classList.toggle('is-open', open);
      nav.setAttribute('aria-hidden', String(!open));
      toggle.setAttribute('aria-expanded', String(open));
      body.classList.toggle('menu-open', open);
      if (open) window.setTimeout(() => focusable(nav)[0]?.focus(), motion().enabled ? 240 : 0);
      else if (menuReturnFocus instanceof HTMLElement) menuReturnFocus.focus();
    };
    const setCartOpen = (open) => {
      if (!cartDrawer || !cartOverlay) return;
      if (open) cartReturnFocus = document.activeElement;
      cartDrawer.classList.toggle('is-open', open);
      cartOverlay.classList.toggle('is-open', open);
      cartDrawer.setAttribute('aria-hidden', String(!open));
      body.classList.toggle('cart-open', open);
      if (open) window.setTimeout(() => cartClose?.focus(), motion().enabled && motion().cart ? 180 : 0);
      else if (cartReturnFocus instanceof HTMLElement) cartReturnFocus.focus();
    };

    on(toggle, 'click', () => setMenuOpen(!nav?.classList.contains('is-open')), signal);
    nav?.querySelectorAll('a').forEach((link) => on(link, 'click', () => setMenuOpen(false), signal));
    on(cartLink, 'click', (event) => { if (cartDrawer) { event.preventDefault(); setCartOpen(true); } }, signal);
    on(cartClose, 'click', () => setCartOpen(false), signal);
    on(cartOverlay, 'click', () => setCartOpen(false), signal);
    on(header, 'click', async (event) => {
      const remove = event.target.closest('[data-cart-remove]');
      const quantity = event.target.closest('[data-cart-quantity]');
      const control = remove || quantity;
      if (!control) return;
      control.disabled = true;
      const line = control.closest('[data-cart-line]');
      line?.classList.add('is-removing');
      try {
        const cart = await changeCartLine(control.dataset.line, remove ? 0 : control.dataset.quantity);
        renderCart(header, cart);
        document.dispatchEvent(new CustomEvent('tarbut:cart-updated', { detail: { cart } }));
      } catch (_) {
        line?.classList.remove('is-removing');
        control.disabled = false;
      }
    }, signal);
    on(header, 'keydown', (event) => {
      if (event.key === 'Escape') {
        if (cartDrawer?.classList.contains('is-open')) setCartOpen(false);
        else if (nav?.classList.contains('is-open')) setMenuOpen(false);
      }
      if (cartDrawer?.classList.contains('is-open')) trapFocus(event, cartDrawer);
      else if (nav?.classList.contains('is-open')) trapFocus(event, nav);
    }, signal);
    on(document, 'tarbut:cart-updated', (event) => renderCart(header, event.detail.cart), signal);
    on(document, 'tarbut:open-cart', () => setCartOpen(true), signal);
    const controller = { destroy: () => abort.abort(), setCartOpen };
    controllers.set(header, controller);
    return controller;
  };

  const initAnnouncement = (announcement) => {
    if (controllers.has(announcement)) return;
    const abort = new AbortController();
    const close = announcement.querySelector('[data-announcement-close]');
    if (!designMode && close && sessionStorage.getItem('tb-announcement') === 'closed') announcement.hidden = true;
    on(close, 'click', () => { announcement.hidden = true; if (!designMode) sessionStorage.setItem('tb-announcement', 'closed'); }, abort.signal);
    controllers.set(announcement, { destroy: () => abort.abort() });
  };

  const initLocaleSelect = (select) => {
    if (controllers.has(select)) return;
    const abort = new AbortController();
    on(select, 'change', () => select.form?.submit(), abort.signal);
    controllers.set(select, { destroy: () => abort.abort() });
  };

  const initLightbox = (dialog) => {
    if (!dialog) return null;
    if (controllers.has(dialog)) return controllers.get(dialog);
    const abort = new AbortController();
    const slides = [...dialog.querySelectorAll('[data-lightbox-slide]')];
    const counter = dialog.querySelector('[data-lightbox-current]');
    const closeButton = dialog.querySelector('[data-lightbox-close]');
    let index = 0;
    let returnFocus = null;
    const show = (nextIndex) => {
      if (!slides.length) return;
      index = (nextIndex + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === index));
      if (counter) counter.textContent = String(index + 1).padStart(2, '0');
    };
    const close = () => {
      if (typeof dialog.close === 'function') dialog.close(); else dialog.removeAttribute('open');
      body.classList.remove('lightbox-open');
      if (returnFocus instanceof HTMLElement) returnFocus.focus();
    };
    const open = (mediaId, trigger) => {
      const targetIndex = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      show(targetIndex < 0 ? 0 : targetIndex);
      returnFocus = trigger;
      if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
      body.classList.add('lightbox-open');
      closeButton?.focus();
    };
    on(closeButton, 'click', close, abort.signal);
    on(dialog.querySelector('[data-lightbox-prev]'), 'click', () => show(index - 1), abort.signal);
    on(dialog.querySelector('[data-lightbox-next]'), 'click', () => show(index + 1), abort.signal);
    on(dialog, 'click', (event) => { if (event.target === dialog) close(); }, abort.signal);
    on(dialog, 'close', () => body.classList.remove('lightbox-open'), abort.signal);
    on(dialog, 'keydown', (event) => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowRight') { event.preventDefault(); show(index + (rtl ? -1 : 1)); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(index + (rtl ? 1 : -1)); }
      trapFocus(event, dialog);
    }, abort.signal);
    const controller = { open, destroy: () => abort.abort() };
    controllers.set(dialog, controller);
    return controller;
  };

  const initProductGallery = (root) => {
    if (controllers.has(root)) return controllers.get(root);
    const abort = new AbortController();
    const slides = [...root.querySelectorAll('[data-gallery-slide]')];
    if (!slides.length) return null;
    const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
    const current = root.querySelector('[data-gallery-current]');
    const viewport = root.querySelector('[data-gallery-viewport]');
    const lightbox = initLightbox(root.closest('[data-product-root]')?.querySelector('[data-media-lightbox]'));
    let index = Math.min(Math.max(Number(root.dataset.initialIndex) || 0, 0), slides.length - 1);
    let pointerStart = null;
    const show = (nextIndex, focusThumb = false) => {
      index = (nextIndex + slides.length) % slides.length;
      root.style.setProperty('--gallery-index', index);
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        if (!active) slide.querySelectorAll('video').forEach((video) => video.pause());
      });
      thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', String(active));
        thumb.tabIndex = active ? 0 : -1;
        if (active) {
          thumb.scrollIntoView({ behavior: motion().enabled ? 'smooth' : 'auto', block: 'nearest', inline: 'nearest' });
          if (focusThumb) thumb.focus();
        }
      });
      if (current) current.textContent = String(index + 1).padStart(2, '0');
    };
    const goToMedia = (mediaId) => {
      if (!mediaId) return;
      const targetIndex = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (targetIndex >= 0) show(targetIndex);
    };
    on(root.querySelector('[data-gallery-prev]'), 'click', () => show(index - 1), abort.signal);
    on(root.querySelector('[data-gallery-next]'), 'click', () => show(index + 1), abort.signal);
    thumbs.forEach((thumb) => on(thumb, 'click', () => show(Number(thumb.dataset.index)), abort.signal));
    root.querySelectorAll('[data-gallery-zoom]').forEach((button) => on(button, 'click', () => lightbox?.open(button.dataset.mediaId, button), abort.signal));
    on(root, 'keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); show(index + (rtl ? -1 : 1), event.target.matches('[data-gallery-thumb]')); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(index + (rtl ? 1 : -1), event.target.matches('[data-gallery-thumb]')); }
      if (event.key === 'Home') { event.preventDefault(); show(0, event.target.matches('[data-gallery-thumb]')); }
      if (event.key === 'End') { event.preventDefault(); show(slides.length - 1, event.target.matches('[data-gallery-thumb]')); }
    }, abort.signal);
    on(viewport, 'pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointerStart = { x: event.clientX, y: event.clientY };
    }, abort.signal);
    on(viewport, 'pointerup', (event) => {
      if (!pointerStart) return;
      const deltaX = event.clientX - pointerStart.x;
      const deltaY = event.clientY - pointerStart.y;
      pointerStart = null;
      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;
      const physicalNext = deltaX < 0;
      show(index + (physicalNext === !rtl ? 1 : -1));
    }, abort.signal);
    on(viewport, 'pointercancel', () => { pointerStart = null; }, abort.signal);
    const controller = { show, goToMedia, getIndex: () => index, destroy: () => abort.abort() };
    controllers.set(root, controller);
    show(index);
    return controller;
  };

  const initProduct = (root) => {
    if (controllers.has(root)) return;
    const abort = new AbortController();
    const { signal } = abort;
    const galleryController = root.querySelector('[data-product-gallery]') ? initProductGallery(root.querySelector('[data-product-gallery]')) : null;
    const variantSelect = root.querySelector('[data-variant-select]');
    const variantOptions = [...root.querySelectorAll('[data-variant-option]')];
    const productForm = root.querySelector('[data-product-form]');
    const addButton = root.querySelector('[data-add-to-cart]');
    const stickyAdd = root.querySelector('[data-sticky-atc]');
    const cartTrigger = root.querySelector('[data-product-cart-trigger]');
    on(cartTrigger, 'click', (event) => {
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('tarbut:open-cart'));
    }, signal);
    variantOptions.forEach((button) => on(button, 'click', () => {
      if (!variantSelect || button.disabled) return;
      variantSelect.value = button.dataset.variantId;
      variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }, signal));
    root.querySelectorAll('[data-quantity-adjust]').forEach((button) => {
      on(button, 'click', () => {
        const input = button.closest('[data-product-quantity]')?.querySelector('input[type="number"]');
        if (!input) return;
        const minimum = Number(input.min) || 1;
        const current = Number(input.value) || minimum;
        input.value = String(Math.max(minimum, current + Number(button.dataset.quantityAdjust || 0)));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }, signal);
    });
    const updateVariantState = () => {
      if (!variantSelect) return;
      const option = variantSelect.options[variantSelect.selectedIndex];
      const available = option.dataset.available === 'true';
      if (addButton) { addButton.disabled = !available; addButton.textContent = available ? addButton.dataset.addLabel : addButton.dataset.soldLabel; }
      const availability = root.querySelector('[data-product-availability]');
      const price = root.querySelector('[data-product-price]');
      const stickyPrice = root.querySelector('.mobile-atc small');
      variantOptions.forEach((button) => {
        const selected = button.dataset.variantId === option.value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', String(selected));
      });
      if (availability) availability.textContent = available ? availability.dataset.inStockLabel : availability.dataset.soldLabel;
      if (price && option.dataset.price) price.textContent = option.dataset.price;
      if (stickyPrice && option.dataset.price) stickyPrice.textContent = option.dataset.price;
      galleryController?.goToMedia(option.dataset.mediaId);
    };
    on(variantSelect, 'change', updateVariantState, signal);
    updateVariantState();
    on(stickyAdd, 'click', () => addButton?.click(), signal);
    if (stickyAdd && productForm) {
      const mobileBar = stickyAdd.closest('.mobile-atc');
      const updateStickyBar = () => mobileBar?.classList.toggle('is-visible', productForm.getBoundingClientRect().bottom < window.innerHeight * .25);
      updateStickyBar();
      on(window, 'scroll', updateStickyBar, signal, { passive: true });
      on(window, 'resize', updateStickyBar, signal);
    }
    on(productForm, 'submit', async (event) => {
      if (!window.fetch || !addButton || addButton.disabled) return;
      event.preventDefault();
      const originalLabel = addButton.dataset.addLabel;
      addButton.disabled = true;
      addButton.classList.add('is-loading');
      addButton.textContent = addButton.dataset.loadingLabel;
      try {
        const response = await fetch(productForm.action, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(productForm) });
        if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);
        const cart = await fetchCart();
        addButton.classList.remove('is-loading');
        addButton.classList.add('is-success');
        addButton.textContent = addButton.dataset.successLabel;
        document.dispatchEvent(new CustomEvent('tarbut:cart-updated', { detail: { cart } }));
        document.dispatchEvent(new CustomEvent('tarbut:open-cart'));
        window.setTimeout(() => { addButton.classList.remove('is-success'); addButton.disabled = false; addButton.textContent = originalLabel; }, 1800);
      } catch (_) {
        addButton.classList.remove('is-loading');
        addButton.classList.add('is-error');
        addButton.textContent = addButton.dataset.errorLabel;
        window.setTimeout(() => { addButton.classList.remove('is-error'); addButton.disabled = false; addButton.textContent = originalLabel; }, 2200);
      }
    }, signal);
    controllers.set(root, { destroy: () => abort.abort() });
  };

  const initCollection = (root) => {
    if (controllers.has(root)) return;
    const abort = new AbortController();
    const toggle = root.querySelector('[data-filter-toggle]');
    const filters = root.querySelector('[data-filters]');
    const cartTrigger = root.querySelector('[data-catalog-cart-trigger]');
    on(toggle, 'click', () => { const open = filters?.classList.toggle('is-open'); toggle.setAttribute('aria-expanded', String(Boolean(open))); }, abort.signal);
    on(root.querySelector('[data-sort-select]'), 'change', (event) => event.target.form?.submit(), abort.signal);
    on(cartTrigger, 'click', (event) => {
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('tarbut:open-cart'));
    }, abort.signal);
    controllers.set(root, { destroy: () => abort.abort() });
  };

  const initMinimalPageNavigation = (root) => {
    if (controllers.has(root)) return;
    const abort = new AbortController();
    on(root.querySelector('[data-page-cart-trigger]'), 'click', (event) => {
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('tarbut:open-cart'));
    }, abort.signal);
    controllers.set(root, { destroy: () => abort.abort() });
  };

  const initCartPage = (form) => {
    if (controllers.has(form)) return;
    const abort = new AbortController();
    on(form, 'click', async (event) => {
      const remove = event.target.closest('[data-cart-page-remove]');
      if (!remove || !window.fetch) return;
      event.preventDefault();
      const line = remove.closest('[data-cart-item]');
      line?.classList.add('is-removing');
      try {
        const cart = await changeCartLine(remove.dataset.line, 0);
        document.dispatchEvent(new CustomEvent('tarbut:cart-updated', { detail: { cart } }));
        window.setTimeout(() => window.location.reload(), motion().enabled ? 220 : 0);
      } catch (_) { window.location.href = remove.href; }
    }, abort.signal);
    on(form, 'submit', (event) => {
      const button = event.submitter;
      if (!button || button.name === 'checkout') return;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
    }, abort.signal);
    controllers.set(form, { destroy: () => abort.abort() });
  };

  const initMediaToggle = (button) => {
    if (controllers.has(button)) return;
    const abort = new AbortController();
    const videos = [...button.parentElement.querySelectorAll('video')];
    on(button, 'click', () => {
      const shouldPlay = videos.every((video) => video.paused);
      videos.forEach((video) => shouldPlay ? video.play() : video.pause());
      button.classList.toggle('is-paused', !shouldPlay);
      button.setAttribute('aria-label', shouldPlay ? button.dataset.pauseLabel : button.dataset.playLabel);
      button.querySelector('span').textContent = shouldPlay ? 'Ⅱ' : '▶';
    }, abort.signal);
    controllers.set(button, { destroy: () => abort.abort() });
  };

  const initMagnetic = (element) => {
    if (controllers.has(element) || !motion().enabled || motion().intensity === 'subtle' || !window.matchMedia('(hover:hover) and (pointer:fine)').matches) return;
    const abort = new AbortController();
    on(element, 'pointermove', (event) => {
      const bounds = element.getBoundingClientRect();
      element.style.setProperty('--magnetic-x', `${(event.clientX - bounds.left - bounds.width / 2) * .12}px`);
      element.style.setProperty('--magnetic-y', `${(event.clientY - bounds.top - bounds.height / 2) * .12}px`);
    }, abort.signal);
    on(element, 'pointerleave', () => { element.style.setProperty('--magnetic-x', '0px'); element.style.setProperty('--magnetic-y', '0px'); }, abort.signal);
    controllers.set(element, { destroy: () => abort.abort() });
  };

  const initEntryPortal = (root) => {
    if (controllers.has(root)) return;
    const abort = new AbortController();
    const { signal } = abort;
    const destinations = [...root.querySelectorAll('[data-entry-target]')];
    const links = destinations.filter((item) => item instanceof HTMLAnchorElement);
    const hoverMedia = [...root.querySelectorAll('[data-entry-hover-media]')];
    const frames = [];
    let navigationTimer = 0;
    let navigationRecoveryTimer = 0;
    const setActive = (destination) => {
      const target = destination?.dataset.entryTarget;
      destinations.forEach((item) => item.classList.toggle('is-active', item === destination));
      hoverMedia.forEach((media) => media.classList.toggle('is-active', Boolean(target) && media.dataset.entryHoverMedia === target));
      root.classList.toggle('is-interacting', Boolean(destination));
    };

    const resetNavigationState = ({ resumeMedia = false } = {}) => {
      window.clearTimeout(navigationTimer);
      window.clearTimeout(navigationRecoveryTimer);
      navigationTimer = 0;
      navigationRecoveryTimer = 0;
      frames.forEach((frame) => window.cancelAnimationFrame(frame));
      destinations.forEach((destination) => destination.classList.remove('is-active', 'is-selected'));
      hoverMedia.forEach((media) => media.classList.remove('is-active'));
      root.classList.remove('is-initializing', 'is-interacting', 'is-leaving');
      root.classList.add('is-ready');

      if (resumeMedia && !reducedMotionQuery.matches) {
        root.querySelectorAll('video[autoplay]').forEach((video) => {
          if (video.paused) video.play().catch(() => {});
        });
      }
    };

    if (motion().enabled && !designMode) {
      root.classList.add('is-initializing');
      frames.push(window.requestAnimationFrame(() => {
        frames.push(window.requestAnimationFrame(() => {
          root.classList.remove('is-initializing');
          root.classList.add('is-ready');
        }));
      }));
    } else {
      root.classList.add('is-ready');
    }

    destinations.forEach((destination) => {
      on(destination, 'pointerenter', () => setActive(destination), signal);
      on(destination, 'pointerleave', () => setActive(null), signal);
      on(destination, 'focusin', () => setActive(destination), signal);
      on(destination, 'focusout', () => setActive(null), signal);
      on(destination, 'pointerdown', () => setActive(destination), signal, { passive: true });
    });

    links.forEach((link) => {
      on(link, 'click', (event) => {
        const isModified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
        const destination = new URL(link.href, window.location.href);
        if (isModified || link.target === '_blank' || link.hasAttribute('download') || destination.origin !== window.location.origin || !motion().enabled || designMode) return;
        event.preventDefault();
        if (root.classList.contains('is-leaving')) return;
        setActive(link);
        link.classList.add('is-selected');
        root.classList.add('is-leaving');
        const duration = Math.max(180, Math.min(Number(root.dataset.transitionDuration) || 700, 1200));
        navigationTimer = window.setTimeout(() => {
          navigationTimer = 0;
          window.location.assign(destination.href);
          navigationRecoveryTimer = window.setTimeout(() => resetNavigationState({ resumeMedia: true }), 1400);
        }, duration * .72);
      }, signal);
    });

    on(window, 'pagehide', () => resetNavigationState(), signal);
    on(window, 'pageshow', (event) => {
      if (event.persisted || root.classList.contains('is-leaving')) {
        resetNavigationState({ resumeMedia: true });
      }
    }, signal);

    on(document, 'shopify:block:select', (event) => {
      const destination = event.target?.closest?.('[data-entry-target]');
      if (destination && root.contains(destination)) setActive(destination);
    }, signal);
    on(document, 'shopify:block:deselect', () => setActive(null), signal);

    controllers.set(root, {
      destroy: () => {
        abort.abort();
        window.clearTimeout(navigationTimer);
        window.clearTimeout(navigationRecoveryTimer);
        frames.forEach((frame) => window.cancelAnimationFrame(frame));
      }
    });
  };

  const initMotionScope = (scope) => {
    motionControllers.get(scope)?.destroy?.();
    const abort = new AbortController();
    let observer = null;
    const state = motion();
    const items = selectAll(scope, '[data-reveal], [data-reveal-item]');
    const imageItems = state.imageReveal ? selectAll(scope, '[data-image-reveal]') : [];
    const targets = [...new Set([...items, ...imageItems])];
    if (!state.enabled || !state.reveal || designMode || !('IntersectionObserver' in window)) {
      targets.forEach((item) => item.classList.add('is-revealed'));
    } else {
      body.classList.add('motion-ready');
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      }, { threshold: .08, rootMargin: '0px 0px -8% 0px' });
      targets.forEach((item) => observer.observe(item));
    }
    selectAll(scope, '[data-hero]').forEach((hero) => window.requestAnimationFrame(() => hero.classList.add('is-loaded')));
    selectAll(scope, '[data-magnetic]').forEach(initMagnetic);
    selectAll(scope, '[data-media-toggle]').forEach(initMediaToggle);
    if (state.enabled && !designMode && state.intensity !== 'subtle' && window.matchMedia('(min-width: 750px)').matches) {
      selectAll(scope, '[data-parallax="true"] img').forEach((image) => {
        let frame = 0;
        const update = () => {
          frame = 0;
          const bounds = image.parentElement.getBoundingClientRect();
          const progress = (window.innerHeight - bounds.top) / (window.innerHeight + bounds.height);
          image.style.setProperty('--parallax-y', `${(progress - .5) * 32}px`);
        };
        const request = () => { if (!frame) frame = window.requestAnimationFrame(update); };
        on(window, 'scroll', request, abort.signal, { passive: true });
        request();
      });
    }
    motionControllers.set(scope, { destroy: () => { observer?.disconnect(); abort.abort(); } });
  };

  const initScope = (scope = document) => {
    selectAll(scope, '[data-header-section]').forEach(initHeader);
    selectAll(scope, '[data-announcement]').forEach(initAnnouncement);
    selectAll(scope, '[data-locale-select]').forEach(initLocaleSelect);
    selectAll(scope, '[data-product-root]').forEach(initProduct);
    selectAll(scope, '.collection-page').forEach(initCollection);
    selectAll(scope, '[data-minimal-page-navigation]').forEach(initMinimalPageNavigation);
    selectAll(scope, '[data-cart-page-form]').forEach(initCartPage);
    selectAll(scope, '[data-entry-portal]').forEach(initEntryPortal);
    initMotionScope(scope);
  };

  const destroyScope = (scope) => {
    [scope, ...scope.querySelectorAll('*')].forEach((element) => {
      const controller = controllers.get(element);
      controller?.destroy?.();
      controllers.delete(element);
      motionControllers.get(element)?.destroy?.();
      motionControllers.delete(element);
    });
  };

  const revealSelected = (target) => {
    target?.classList?.add('is-revealed');
    target?.querySelectorAll?.('[data-reveal], [data-reveal-item], [data-image-reveal]').forEach((item) => item.classList.add('is-revealed'));
    target?.scrollIntoView?.({ behavior: reducedMotionQuery.matches ? 'auto' : 'smooth', block: 'center' });
  };

  initScope(document);
  document.addEventListener('shopify:section:load', (event) => initScope(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyScope(event.target));
  document.addEventListener('shopify:section:select', (event) => revealSelected(event.target));
  document.addEventListener('shopify:block:select', (event) => revealSelected(event.target));
})();
